import React, { useEffect, useState, useCallback } from 'react';
import { useApp, api, MEDIA } from '@/context/AppContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Search, Filter, X, Sparkles, Loader2, Zap, FileText, BookOpen, Layers, Activity, Film, Image as ImageIcon, ClipboardCheck, ClipboardList, FileEdit, Home, Award, HelpCircle, Heart, MessageCircle, Gamepad2, Library } from 'lucide-react';
import { toast } from 'sonner';
import { ArtifactView } from './AIStudioPage';

export default function CurriculumLibrary() {
  const { t, lang } = useApp();
  const [filters, setFilters] = useState({});
  const [opts, setOpts] = useState({});
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const limit = 24;
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => { api.get('/curriculum/filters').then(r => setOpts(r.data)); }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip, limit, q: q || undefined, ...filters };
      Object.keys(params).forEach(k => params[k] === '' && delete params[k]);
      const { data } = await api.get('/curriculum/goals', { params });
      setItems(data.items);
      setTotal(data.total);
    } finally { setLoading(false); }
  }, [q, filters, skip]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const setFilter = (k, v) => { setSkip(0); setFilters(prev => ({ ...prev, [k]: v === '__all__' ? '' : v })); };
  const clear = () => { setQ(''); setFilters({}); setSkip(0); };

  const goalText = (g) => lang === 'ar' ? g.main_goal_ar : (g.main_goal_en || g.main_goal_ar);
  const labelKey = (val, key_ar, key_en) => lang === 'ar' ? (val[key_ar] || '') : (val[key_en] || val[key_ar] || '');

  return (
    <main id="main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="curriculum-page">
      <div className="mb-6">
        <h1 className="text-3xl sm:text-4xl font-black mb-2">{t('curriculum_title')}</h1>
        <p className="text-muted-foreground">{total.toLocaleString()} {t('stats_goals')}</p>
      </div>

      <Card className="mb-6">
        <CardContent className="p-4 space-y-4">
          <div className="relative">
            <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-muted-foreground" aria-hidden="true" />
            <Input
              placeholder={t('search_placeholder')}
              value={q}
              onChange={(e) => { setQ(e.target.value); setSkip(0); }}
              className="ps-10 h-11"
              data-testid="search-input"
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            <FilterSelect label={t('filter_disability')} options={opts.disability_types} value={filters.disability_type} onChange={(v) => setFilter('disability_type', v)} testid="filter-disability" />
            <FilterSelect label={t('filter_level')} options={opts.disability_levels} value={filters.disability_level} onChange={(v) => setFilter('disability_level', v)} testid="filter-level" />
            <FilterSelect label={t('filter_age')} options={opts.age_groups} value={filters.age_group} onChange={(v) => setFilter('age_group', v)} testid="filter-age" />
            <FilterSelect label={t('filter_term')} options={opts.terms} value={filters.term} onChange={(v) => setFilter('term', v)} testid="filter-term" />
            <FilterSelect label={t('filter_sport')} options={opts.sports} value={filters.sport} onChange={(v) => setFilter('sport', v)} testid="filter-sport" />
            <FilterSelect label={t('filter_perf')} options={opts.performance_levels} value={filters.performance_level} onChange={(v) => setFilter('performance_level', v)} testid="filter-perf" />
          </div>

          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={clear} data-testid="clear-filters"><X className="w-4 h-4 me-1" />{t('clear_filters')}</Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-center py-16 text-muted-foreground">{t('loading')}</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">{t('no_results')}</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((g) => (
            <button key={g.id} onClick={() => setSelected(g)} data-testid={`goal-card-${g.goal_code}`} className="text-start card-elevate bg-white border border-border rounded-xl p-5 hover:border-primary transition-colors">
              <div className="flex items-start justify-between gap-2 mb-3">
                <Badge variant="secondary" className="font-mono text-xs">{g.goal_code}</Badge>
                <Badge className="bg-primary/10 text-primary border-primary/20" variant="outline">{labelKey(g, 'performance_level_ar', 'performance_level_ar')}</Badge>
              </div>
              <h3 className="font-bold text-base mb-3 line-clamp-2 leading-snug">{goalText(g)}</h3>
              <div className="flex flex-wrap gap-1.5">
                <Badge variant="outline" className="text-xs">{labelKey(g, 'disability_type_ar', 'disability_type_en')}</Badge>
                <Badge variant="outline" className="text-xs">{labelKey(g, 'age_group_ar', 'age_group_en')}</Badge>
                <Badge variant="outline" className="text-xs">{labelKey(g, 'sport_activity_ar', 'sport_activity_en')}</Badge>
              </div>
            </button>
          ))}
        </div>
      )}

      {total > limit && (
        <div className="flex items-center justify-between mt-8">
          <Button variant="outline" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} data-testid="page-prev">{t('prev')}</Button>
          <span className="text-sm text-muted-foreground">{t('showing')} {skip + 1}–{Math.min(skip + limit, total)} {t('of')} {total.toLocaleString()}</span>
          <Button variant="outline" disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)} data-testid="page-next">{t('next')}</Button>
        </div>
      )}

      <GoalDetailDialog goal={selected} onClose={() => setSelected(null)} t={t} lang={lang} />
    </main>
  );
}

function FilterSelect({ label, options = [], value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-muted-foreground mb-1 block">{label}</label>
      <Select value={value || '__all__'} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="h-10">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">—</SelectItem>
          {options.filter(Boolean).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

// ============================================================
// GOAL DETAIL DIALOG — Tabbed: Details | AI Studio | Magic Pack | Saved
// ============================================================
// 2) GOAL DETAIL DIALOG — Tabbed interface (Details / AI / Pack / Saved)
// ============================================================
function GoalDetailDialog({ goal: initialGoal, onClose, t, lang }) {
  const [goal, setGoal] = useState(initialGoal);
  const [translating, setTranslating] = useState(false);

  // Keep local goal in sync if parent provides a new one
  useEffect(() => { setGoal(initialGoal); }, [initialGoal]);

  // LAZY TRANSLATION: when dialog opens in English but English fields are missing,
  // request a one-shot translation of all translatable fields and refresh the dialog.
  useEffect(() => {
    if (!goal || lang !== 'en') return;
    const needsTranslation = !goal.teaching_strategy_en && !!goal.teaching_strategy_ar;
    if (!needsTranslation) return;
    let cancelled = false;
    setTranslating(true);
    api.post(`/curriculum/translate-goal/${goal.id}`)
      .then(r => { if (!cancelled && r.data?.goal) setGoal(r.data.goal); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setTranslating(false); });
    return () => { cancelled = true; };
  }, [goal?.id, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!goal) return null;
  const txt = (ar, en) => lang === 'ar' ? ar : (en || ar);
  return (
    <Dialog open={!!goal} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[94vh] overflow-hidden p-0 flex flex-col" data-testid="goal-detail-dialog">
        {/* Sticky header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border bg-gradient-to-br from-primary/8 via-primary/3 to-transparent shrink-0">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Badge variant="secondary" className="font-mono text-[11px] px-2.5">{goal.goal_code}</Badge>
            <Badge className="bg-primary text-primary-foreground text-[11px]">{txt(goal.performance_level_ar, goal.performance_level_en)}</Badge>
            <Badge variant="outline" className="text-[11px]">{txt(goal.disability_type_ar, goal.disability_type_en)}</Badge>
            <Badge variant="outline" className="text-[11px]">{txt(goal.age_group_ar, goal.age_group_en)}</Badge>
            <Badge variant="outline" className="text-[11px]">{txt(goal.sport_activity_ar, goal.sport_activity_en)}</Badge>
            {translating && (
              <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]" data-testid="translating-badge">
                <Loader2 className="w-3 h-3 me-1 animate-spin" />
                {lang === 'ar' ? 'يتم الترجمة...' : 'Translating to English...'}
              </Badge>
            )}
          </div>
          <DialogTitle className="text-xl sm:text-2xl text-start leading-snug font-black">{txt(goal.main_goal_ar, goal.main_goal_en)}</DialogTitle>
          {goal.sub_goal_ar && (
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{txt(goal.sub_goal_ar, goal.sub_goal_en)}</p>
          )}
          <DialogDescription className="sr-only">Goal details and AI generators</DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        <Tabs defaultValue="details" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="mx-6 mt-3 self-start bg-secondary/60 shrink-0" data-testid="goal-tabs">
            <TabsTrigger value="details" data-testid="tab-goal-details"><FileText className="w-3.5 h-3.5 me-1.5" />{lang === 'ar' ? 'التفاصيل' : 'Details'}</TabsTrigger>
            <TabsTrigger value="ai" data-testid="tab-goal-ai" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><Sparkles className="w-3.5 h-3.5 me-1.5" />{lang === 'ar' ? 'مولّدات AI' : 'AI Generators'}</TabsTrigger>
            <TabsTrigger value="pack" data-testid="tab-goal-pack">⚡ {lang === 'ar' ? 'منظومة كاملة' : 'Full Pack'}</TabsTrigger>
            <TabsTrigger value="saved" data-testid="tab-goal-saved"><Library className="w-3.5 h-3.5 me-1.5" />{lang === 'ar' ? 'المحفوظ' : 'Saved'}</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <TabsContent value="details" className="m-0"><GoalDetailsTab goal={goal} t={t} lang={lang} translating={translating} /></TabsContent>
            <TabsContent value="ai" className="m-0"><GoalAIGeneratorsTab goal={goal} /></TabsContent>
            <TabsContent value="pack" className="m-0"><GoalMagicPackTab goal={goal} /></TabsContent>
            <TabsContent value="saved" className="m-0"><GoalSavedTab goal={goal} /></TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// ----- TAB 1: DETAILS -----
function GoalDetailsTab({ goal, t, lang, translating }) {
  // Use bilingual fallback: when lang==='en' prefer _en; if missing, fallback to _ar.
  const tv = (ar, en) => lang === 'ar' ? ar : (en || ar);
  return (
    <div className="space-y-4 text-sm">
      {translating && lang === 'en' && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-center gap-2 text-amber-800 text-xs" data-testid="translating-banner">
          <Loader2 className="w-4 h-4 animate-spin" />
          Translating goal content to English… this happens once per goal and is cached afterwards.
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <DetailRow label={t('teaching_strategy')} value={tv(goal.teaching_strategy_ar, goal.teaching_strategy_en)} />
        <DetailRow label={t('tools')} value={tv(goal.tools_ar, goal.tools_en)} />
        <DetailRow label={t('environment')} value={tv(goal.environment_ar, goal.environment_en)} />
        <DetailRow label={lang === 'ar' ? 'المعززات' : 'Reinforcers'} value={tv(goal.reinforcers_ar, goal.reinforcers_en)} />
        <DetailRow label={lang === 'ar' ? 'نوع الجلسة' : 'Session type'} value={tv(goal.session_type_ar, goal.session_type_en)} />
        <DetailRow label={lang === 'ar' ? 'نوع التقويم' : 'Assessment type'} value={tv(goal.assessment_type_ar, goal.assessment_type_en)} />
        <DetailRow label={lang === 'ar' ? 'حجم المجموعة' : 'Group size'} value={tv(goal.group_size_ar, goal.group_size_en)} />
        <DetailRow label={t('filter_term')} value={tv(goal.term_ar, goal.term_en)} />
      </div>
      <Section title={lang === 'ar' ? 'خطة التمهيد' : 'Intro plan'} content={tv(goal.intro_plan_ar, goal.intro_plan_en)} />
      <Section title={t('warmup')} content={tv(goal.warmup_plan_ar, goal.warmup_plan_en)} />
      <Section title={t('main_exercises') + ' 1'} content={tv(goal.main_ex1_ar, goal.main_ex1_en)} />
      <Section title={t('main_exercises') + ' 2'} content={tv(goal.main_ex2_ar, goal.main_ex2_en)} />
      <Section title={t('main_exercises') + ' 3'} content={tv(goal.main_ex3_ar, goal.main_ex3_en)} />
      <Section title={t('cooldown')} content={tv(goal.cooldown_ar, goal.cooldown_en)} />
      {goal.full_session_plan_ar && <Section title={lang === 'ar' ? 'الخطة الكاملة' : 'Full plan'} content={tv(goal.full_session_plan_ar, goal.full_session_plan_en)} />}
      {goal.adapted_below_30_ar && <Section title={lang === 'ar' ? 'تكييف لمن أداؤه < ٣٠٪' : 'Adapted < 30%'} content={tv(goal.adapted_below_30_ar, goal.adapted_below_30_en)} />}
      {goal.adapted_enrichment_ar && <Section title={lang === 'ar' ? 'تكييف إثرائي ٣٠–٥٠٪' : 'Enrichment 30-50%'} content={tv(goal.adapted_enrichment_ar, goal.adapted_enrichment_en)} />}
    </div>
  );
}

// ----- TAB 2: AI GENERATORS GRID — 16 inline generators -----
const GENERATORS_CATALOG = [
  { key: 'lesson_plan', endpoint: '/ai/lesson-plan', icon: FileText, color: 'bg-emerald-50 text-emerald-700 border-emerald-200', label_ar: 'خطة درس', label_en: 'Lesson Plan', extra_fields: [] },
  { key: 'activity', endpoint: '/ai/activity', icon: Activity, color: 'bg-sky-50 text-sky-700 border-sky-200', label_ar: 'أنشطة', label_en: 'Activities', extra_fields: [] },
  { key: 'flashcards', endpoint: '/ai/flashcards', icon: Layers, color: 'bg-amber-50 text-amber-700 border-amber-200', label_ar: 'بطاقات', label_en: 'Flashcards',
    extra_fields: [{ name: 'count', type: 'number', default: 6, min: 3, max: 12, label_ar: 'العدد', label_en: 'Count' }, { name: 'with_images', type: 'bool', default: false, label_ar: 'مع صور', label_en: 'With images' }] },
  { key: 'story', endpoint: '/ai/story', icon: BookOpen, color: 'bg-purple-50 text-purple-700 border-purple-200', label_ar: 'قصة مصوّرة', label_en: 'Story',
    extra_fields: [{ name: 'scenes', type: 'number', default: 3, min: 2, max: 5, label_ar: 'المشاهد', label_en: 'Scenes' }, { name: 'with_images', type: 'bool', default: true, label_ar: 'مع صور', label_en: 'With images' }, { name: 'with_audio', type: 'bool', default: false, label_ar: 'مع صوت', label_en: 'With audio' }] },
  { key: 'quiz', endpoint: '/ai/quiz', icon: HelpCircle, color: 'bg-violet-50 text-violet-700 border-violet-200', label_ar: 'اختبار', label_en: 'Quiz',
    extra_fields: [{ name: 'questions', type: 'number', default: 5, min: 3, max: 10, label_ar: 'الأسئلة', label_en: 'Questions' }] },
  { key: 'assessment', endpoint: '/ai/assessment', icon: ClipboardCheck, color: 'bg-teal-50 text-teal-700 border-teal-200', label_ar: 'روبرك تقييم', label_en: 'Rubric', extra_fields: [] },
  { key: 'iep', endpoint: '/ai/iep', icon: ClipboardList, color: 'bg-indigo-50 text-indigo-700 border-indigo-200', label_ar: 'خطة فردية IEP', label_en: 'IEP',
    extra_fields: [{ name: 'student_name', type: 'text', required: true, label_ar: 'اسم الطالب', label_en: 'Student name' }] },
  { key: 'worksheet', endpoint: '/ai/worksheet', icon: FileEdit, color: 'bg-orange-50 text-orange-700 border-orange-200', label_ar: 'ورقة عمل', label_en: 'Worksheet', extra_fields: [] },
  { key: 'adaptive', endpoint: '/ai/adaptive-activities', icon: Heart, color: 'bg-red-50 text-red-700 border-red-200', label_ar: 'تكيفات ×٥ فئات', label_en: 'Adaptive ×5', extra_fields: [] },
  { key: 'family', endpoint: '/ai/family-activities', icon: Home, color: 'bg-pink-50 text-pink-700 border-pink-200', label_ar: 'أنشطة أسرية', label_en: 'Family',
    extra_fields: [{ name: 'child_age', type: 'text', default: '7', label_ar: 'عمر الطفل', label_en: 'Child age' }] },
  { key: 'video_script', endpoint: '/ai/video-script', icon: Film, color: 'bg-rose-50 text-rose-700 border-rose-200', label_ar: 'سيناريو فيديو', label_en: 'Video Storyboard',
    extra_fields: [{ name: 'frames', type: 'number', default: 4, min: 4, max: 8, label_ar: 'لقطات', label_en: 'Frames' }, { name: 'with_audio', type: 'bool', default: false, label_ar: 'مع صوت', label_en: 'With audio' }] },
  { key: 'visual_demo', endpoint: '/ai/visual-demo', icon: ImageIcon, color: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200', label_ar: 'صور توضيحية', label_en: 'Visual Demo',
    extra_fields: [{ name: 'count', type: 'number', default: 2, min: 1, max: 4, label_ar: 'العدد', label_en: 'Count' }] },
  { key: 'certificate', endpoint: '/ai/certificate', icon: Award, color: 'bg-yellow-50 text-yellow-700 border-yellow-200', label_ar: 'شهادة', label_en: 'Certificate',
    extra_fields: [{ name: 'recipient_name', type: 'text', required: true, label_ar: 'اسم المستلم', label_en: 'Recipient' }, { name: 'achievement', type: 'text', required: true, label_ar: 'الإنجاز', label_en: 'Achievement' }] },
  { key: 'coach', endpoint: '/ai/coach', icon: MessageCircle, color: 'bg-cyan-50 text-cyan-700 border-cyan-200', label_ar: 'سؤال للكوتش', label_en: 'Coach',
    extra_fields: [{ name: 'question', type: 'longtext', required: true, label_ar: 'السؤال', label_en: 'Question' }, { name: 'with_audio', type: 'bool', default: false, label_ar: 'مع صوت', label_en: 'With audio' }] },
  { key: 'game', endpoint: '/ai/game', icon: Gamepad2, color: 'bg-lime-50 text-lime-700 border-lime-200', label_ar: 'لعبة تعليمية', label_en: 'Game', extra_fields: [] },
];

// Mapping from generator key → artifact kind stored in DB (for /library/by-goal lookup)
const KIND_BY_GEN = {
  lesson_plan: 'lesson_plan', activity: 'activity', flashcards: 'flashcards', story: 'story',
  quiz: 'quiz', assessment: 'assessment', iep: 'iep', worksheet: 'worksheet',
  adaptive: 'adaptive_activities', family: 'family_activities', video_script: 'video_script',
  visual_demo: 'visual_demo', certificate: 'certificate', coach: 'coach', game: 'game',
};

function GoalAIGeneratorsTab({ goal }) {
  const { lang } = useApp();
  const [active, setActive] = useState(null);

  return (
    <div data-testid="goal-ai-grid">
      <div className="bg-gradient-to-br from-primary/5 to-accent/5 border border-primary/20 rounded-xl p-4 mb-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/15 grid place-items-center shrink-0"><Sparkles className="w-5 h-5 text-primary" /></div>
        <div className="text-sm">
          <div className="font-bold mb-0.5">{lang === 'ar' ? '١٥ مولّداً ذكياً جاهز لهذا الهدف' : '15 AI generators ready for this goal'}</div>
          <div className="text-muted-foreground text-xs">{lang === 'ar' ? 'كل المخرجات مرتبطة برمز الهدف وتُحفظ تلقائياً في تبويب "المحفوظ"' : 'All outputs are linked to the goal code and auto-saved to the "Saved" tab'}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
        {GENERATORS_CATALOG.map(g => {
          const Icon = g.icon;
          return (
            <button
              key={g.key}
              onClick={() => setActive(g)}
              data-testid={`inline-gen-${g.key}`}
              className={`relative text-start p-3 rounded-xl border-2 ${g.color} hover:shadow-md hover:scale-[1.02] transition-all`}
            >
              <Icon className="w-5 h-5 mb-2" aria-hidden="true" />
              <div className="font-bold text-xs leading-tight">{lang === 'ar' ? g.label_ar : g.label_en}</div>
            </button>
          );
        })}
      </div>

      {active && <InlineGeneratorRunner generator={active} goal={goal} onClose={() => setActive(null)} />}
    </div>
  );
}

function InlineGeneratorRunner({ generator, goal, onClose }) {
  const { lang, t } = useApp();
  const [form, setForm] = useState(() => {
    const init = { goal_id: goal.id, language: lang };
    (generator.extra_fields || []).forEach(f => {
      if (f.default !== undefined) init[f.name] = f.default;
      else if (f.type === 'bool') init[f.name] = false;
      else init[f.name] = '';
    });
    return init;
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const labelOf = (f) => lang === 'ar' ? f.label_ar : f.label_en;

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(generator.endpoint, form);
      setResult({ ...data, _kind: KIND_BY_GEN[generator.key] || generator.key });
      toast.success(lang === 'ar' ? 'تم التوليد وحُفظ' : 'Generated & saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || t('error'));
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-5 border-2 border-primary/30 rounded-xl bg-white shadow-lg overflow-hidden" data-testid="inline-runner">
      <div className="bg-primary/5 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="font-bold text-sm">{lang === 'ar' ? generator.label_ar : generator.label_en}</span>
          <Badge variant="secondary" className="font-mono text-[10px] ms-1">{goal.goal_code}</Badge>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-secondary rounded" aria-label="close" data-testid="inline-runner-close">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-4">
        {!result ? (
          <>
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-2.5 mb-3 text-[11px] text-muted-foreground">
              <strong className="text-primary">{lang === 'ar' ? '✓ مربوط تلقائياً' : '✓ Auto-linked'}:</strong> {lang === 'ar' ? 'الهدف، الفئة العمرية، نوع الإعاقة، الرياضة، ومستوى الأداء' : 'Goal, age group, disability, sport, performance level'}
            </div>

            {(generator.extra_fields || []).length > 0 && (
              <div className="grid sm:grid-cols-2 gap-3 mb-4">
                {generator.extra_fields.map(f => (
                  <div key={f.name}>
                    <Label className="text-xs">{labelOf(f)}{f.required ? ' *' : ''}</Label>
                    {f.type === 'longtext' ? (
                      <Textarea rows={2} value={form[f.name] || ''} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`inline-field-${f.name}`} />
                    ) : f.type === 'number' ? (
                      <Input type="number" min={f.min} max={f.max} value={form[f.name] ?? ''} onChange={(e) => setForm({ ...form, [f.name]: Number(e.target.value) })} data-testid={`inline-field-${f.name}`} />
                    ) : f.type === 'bool' ? (
                      <div className="flex items-center gap-2 pt-1"><Switch checked={!!form[f.name]} onCheckedChange={(v) => setForm({ ...form, [f.name]: v })} data-testid={`inline-field-${f.name}`} /><span className="text-xs">{form[f.name] ? 'ON' : 'OFF'}</span></div>
                    ) : (
                      <Input value={form[f.name] || ''} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`inline-field-${f.name}`} />
                    )}
                  </div>
                ))}
              </div>
            )}

            <Button onClick={run} disabled={busy} className="w-full bg-primary hover:bg-primary/90 h-11" data-testid="inline-run-btn">
              {busy ? <><Loader2 className="w-4 h-4 me-2 animate-spin" />{lang === 'ar' ? 'جارٍ التوليد...' : 'Generating...'}</> : <><Sparkles className="w-4 h-4 me-2" />{lang === 'ar' ? 'توليد' : 'Generate'}</>}
            </Button>
          </>
        ) : (
          <div data-testid="inline-result">
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <Badge className="bg-primary text-primary-foreground">✓ {lang === 'ar' ? 'تم التوليد' : 'Generated'}</Badge>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="inline-copy-btn"
                  onClick={async () => {
                    const { artifactToText } = await import('@/lib/artifact-export');
                    const text = artifactToText(result, lang);
                    try {
                      await navigator.clipboard.writeText(text);
                      toast.success(lang === 'ar' ? 'تم النسخ' : 'Copied');
                    } catch {
                      toast.error(lang === 'ar' ? 'فشل النسخ' : 'Copy failed');
                    }
                  }}
                >
                  📋 {lang === 'ar' ? 'نسخ كل النتائج' : 'Copy all'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="inline-pdf-btn"
                  onClick={async () => {
                    const { exportPackPdf } = await import('@/lib/artifact-export');
                    exportPackPdf({ artifacts: { [result._kind || generator.key]: result }, goal, lang });
                  }}
                >
                  📄 PDF
                </Button>
                <Button variant="outline" size="sm" onClick={() => setResult(null)} data-testid="inline-again">{lang === 'ar' ? 'توليد جديد' : 'New'}</Button>
              </div>
            </div>
            <ArtifactView a={result} />
          </div>
        )}
      </div>
    </div>
  );
}

// ----- TAB 3: MAGIC PACK -----
const PACK_CATALOG = [
  { key: 'lesson_plan', icon: '📋', label_ar: 'خطة الدرس', label_en: 'Lesson Plan' },
  { key: 'flashcards', icon: '🎴', label_ar: 'بطاقات تعليمية', label_en: 'Flashcards' },
  { key: 'quiz', icon: '❓', label_ar: 'اختبار MCQ', label_en: 'MCQ Quiz' },
  { key: 'assessment', icon: '✅', label_ar: 'روبرك تقييم', label_en: 'Rubric' },
  { key: 'adaptive', icon: '♿', label_ar: 'أنشطة تكيفية ×٥', label_en: 'Adaptive ×5' },
  { key: 'worksheet', icon: '📝', label_ar: 'ورقة عمل', label_en: 'Worksheet' },
  { key: 'story', icon: '📖', label_ar: 'قصة مصوّرة', label_en: 'Story (with images)' },
];

function GoalMagicPackTab({ goal }) {
  const { t, lang } = useApp();
  const [selected, setSelected] = useState(['lesson_plan', 'flashcards', 'quiz', 'assessment', 'adaptive']);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const toggle = (k) => setSelected(selected.includes(k) ? selected.filter(x => x !== k) : [...selected, k]);

  const generate = async () => {
    if (selected.length === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post('/ai/goal-pack', { goal_id: goal.id, include: selected, language: lang });
      setResult(data);
      toast.success(lang === 'ar' ? 'تم توليد المنظومة' : 'Pack generated');
    } catch (e) {
      toast.error(e.response?.data?.detail || t('error'));
    } finally { setBusy(false); }
  };

  if (result) return <PackResult result={result} lang={lang} onAgain={() => setResult(null)} goal={goal} />;

  return (
    <div data-testid="goal-magic-pack">
      <div className="bg-gradient-to-br from-primary to-primary/85 text-primary-foreground rounded-xl p-5 mb-5 relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-20" aria-hidden="true" />
        <div className="relative flex items-start gap-4">
          <div className="text-4xl">⚡</div>
          <div>
            <h3 className="font-black text-base sm:text-lg mb-1">{lang === 'ar' ? 'منظومة المحتوى الكاملة' : 'Complete Content Pack'}</h3>
            <p className="text-xs text-primary-foreground/90 leading-relaxed">{lang === 'ar' ? 'اختر العناصر المطلوبة وستُولَّد جميعها بالتوازي في ٣٠–٩٠ ثانية، ومرتبطة برمز الهدف.' : 'Pick items and they will all generate in parallel in 30-90s, linked to the goal code.'}</p>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
        {PACK_CATALOG.map(item => {
          const on = selected.includes(item.key);
          return (
            <button
              key={item.key}
              onClick={() => toggle(item.key)}
              data-testid={`pack-toggle-${item.key}`}
              className={`text-start p-3 rounded-xl border-2 transition-all ${on ? 'border-primary bg-primary/8 shadow-md' : 'border-border bg-white hover:border-primary/40'}`}
            >
              <div className="flex items-start gap-3">
                <div className="text-2xl">{item.icon}</div>
                <div className="flex-1">
                  <div className="font-bold text-sm">{lang === 'ar' ? item.label_ar : item.label_en}</div>
                  <div className={`text-[10px] mt-1 font-semibold ${on ? 'text-primary' : 'text-muted-foreground'}`}>{on ? (lang === 'ar' ? '✓ مُختار' : '✓ Selected') : (lang === 'ar' ? 'انقر للاختيار' : 'Click to select')}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <Button onClick={generate} disabled={busy || selected.length === 0} className="w-full h-12 bg-primary hover:bg-primary/90 text-base font-bold shadow-lg" data-testid="pack-generate-btn">
        {busy ? <><Loader2 className="w-4 h-4 me-2 animate-spin" />{lang === 'ar' ? 'جارٍ التوليد...' : 'Generating...'}</> : (lang === 'ar' ? `⚡ ولّد ${selected.length} عنصر بالذكاء الاصطناعي` : `⚡ Generate ${selected.length} item(s)`)}
      </Button>
    </div>
  );
}

function PackResult({ result, lang, onAgain, goal }) {
  const { artifacts = {} } = result;
  const validArtifacts = Object.fromEntries(Object.entries(artifacts).filter(([_, v]) => v && !v.error));

  const copyAll = async () => {
    const { artifactToText, kindLabel } = await import('@/lib/artifact-export');
    const text = Object.entries(validArtifacts)
      .map(([kind, art]) => `═══ ${kindLabel(kind, lang)} ═══\n${artifactToText(art, lang)}`)
      .join('\n\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success(lang === 'ar' ? 'تم نسخ كل المحتوى' : 'All content copied');
    } catch {
      toast.error(lang === 'ar' ? 'فشل النسخ' : 'Copy failed');
    }
  };

  const exportPdf = async () => {
    const { exportPackPdf } = await import('@/lib/artifact-export');
    exportPackPdf({ artifacts: validArtifacts, goal, lang });
  };

  return (
    <div className="space-y-3" data-testid="pack-result">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="text-xs text-muted-foreground">{lang === 'ar' ? `تم توليد ${Object.keys(artifacts).length} عنصر · يمكن مشاهدتها كاملةً من تبويب "المحفوظ"` : `Generated ${Object.keys(artifacts).length} items · view full content in "Saved" tab`}</div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={copyAll} data-testid="pack-copy-all">📋 {lang === 'ar' ? 'نسخ الكل' : 'Copy all'}</Button>
          <Button variant="outline" size="sm" onClick={exportPdf} data-testid="pack-export-pdf">📄 {lang === 'ar' ? 'تصدير PDF' : 'Export PDF'}</Button>
        </div>
      </div>
      {Object.entries(artifacts).map(([kind, art]) => {
        const item = PACK_CATALOG.find(c => c.key === kind);
        const hasError = art?.error;
        return (
          <div key={kind} className={`border rounded-lg p-3 ${hasError ? 'border-destructive/40 bg-destructive/5' : 'border-primary/30 bg-primary/5'}`} data-testid={`pack-item-${kind}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl">{item?.icon || '✨'}</span>
              <span className="font-bold text-sm">{item ? (lang === 'ar' ? item.label_ar : item.label_en) : kind}</span>
              {hasError ? (
                <Badge variant="destructive" className="ms-auto text-[10px]">{lang === 'ar' ? 'فشل' : 'Failed'}</Badge>
              ) : (
                <Badge className="bg-primary text-primary-foreground ms-auto text-[10px]">✓ {lang === 'ar' ? 'تم' : 'Done'}</Badge>
              )}
            </div>
            {hasError ? (
              <div className="text-xs text-destructive">{art.error}</div>
            ) : art?.content ? (
              <div className="text-xs text-muted-foreground line-clamp-3 whitespace-pre-wrap">{art.content}</div>
            ) : art?.title ? (
              <div className="text-xs text-muted-foreground">{art.title}</div>
            ) : (
              <div className="text-xs text-muted-foreground">{lang === 'ar' ? 'محفوظ' : 'Saved'}</div>
            )}
          </div>
        );
      })}
      <Button variant="outline" onClick={onAgain} className="w-full" data-testid="pack-again">{lang === 'ar' ? 'توليد جديد' : 'Generate new'}</Button>
    </div>
  );
}

// ----- TAB 4: SAVED ARTIFACTS LINKED TO THIS GOAL -----
function GoalSavedTab({ goal }) {
  const { lang } = useApp();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    api.get(`/ai/library/by-goal/${goal.id}`).then(r => setItems(r.data)).catch(() => setItems([]));
  }, [goal.id]);

  useEffect(() => { load(); }, [load]);

  if (items === null) return <div className="text-center py-8 text-muted-foreground text-sm">{lang === 'ar' ? 'جارٍ التحميل...' : 'Loading...'}</div>;
  if (items.length === 0) return (
    <div className="text-center py-12 border-2 border-dashed border-border rounded-xl">
      <Library className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
      <p className="text-sm text-muted-foreground">{lang === 'ar' ? 'لم يتم توليد أي محتوى لهذا الهدف بعد' : 'No content generated for this goal yet'}</p>
      <p className="text-xs text-muted-foreground mt-1">{lang === 'ar' ? 'استخدم تبويب "مولّدات AI" أو "منظومة كاملة"' : 'Use "AI Generators" or "Full Pack" tabs'}</p>
    </div>
  );

  return (
    <div data-testid="goal-saved-grid">
      <div className="mb-3 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs text-muted-foreground"><strong className="text-foreground">{items.length}</strong> {lang === 'ar' ? 'عنصر محفوظ لهذا الهدف' : 'item(s) saved for this goal'}</div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            data-testid="saved-export-all-pdf"
            onClick={async () => {
              const { exportPackPdf } = await import('@/lib/artifact-export');
              const byKind = {};
              items.forEach((it, idx) => { byKind[`${it.kind}_${idx}`] = it; });
              exportPackPdf({ artifacts: byKind, goal, lang });
            }}
          >📄 {lang === 'ar' ? 'تصدير الكل PDF' : 'Export all PDF'}</Button>
          <Button size="sm" variant="ghost" onClick={load} className="text-xs h-8" data-testid="saved-refresh">↻ {lang === 'ar' ? 'تحديث' : 'Refresh'}</Button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
        {items.map(a => (
          <button
            key={a.id}
            onClick={() => setOpen(a)}
            data-testid={`saved-item-${a.id}`}
            className="text-start border border-border rounded-lg p-3 hover:border-primary hover:shadow-md transition-all bg-white"
          >
            <Badge variant="secondary" className="text-[10px] mb-1.5">{a.kind}</Badge>
            <div className="font-bold text-xs line-clamp-2">{a.title || a.name || a.topic || a.recipient_name || a.kind}</div>
            <div className="text-[10px] text-muted-foreground mt-1.5">{new Date(a.created_at).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-US')}</div>
          </button>
        ))}
      </div>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="saved-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-2">
              <span>{open?.title || open?.name || open?.kind}</span>
              {open && (
                <div className="flex gap-2 me-6">
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="saved-copy-btn"
                    onClick={async () => {
                      const { artifactToText } = await import('@/lib/artifact-export');
                      try { await navigator.clipboard.writeText(artifactToText(open, lang)); toast.success(lang === 'ar' ? 'تم النسخ' : 'Copied'); } catch { toast.error('error'); }
                    }}
                  >📋 {lang === 'ar' ? 'نسخ' : 'Copy'}</Button>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="saved-pdf-btn"
                    onClick={async () => {
                      const { exportPackPdf } = await import('@/lib/artifact-export');
                      exportPackPdf({ artifacts: { [open.kind]: open }, goal, lang });
                    }}
                  >📄 PDF</Button>
                </div>
              )}
            </DialogTitle>
            <DialogDescription className="sr-only">Saved artifact</DialogDescription>
          </DialogHeader>
          {open && <ArtifactView a={open} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailRow({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-xs font-semibold text-muted-foreground mb-1">{label}</div>
      <div className="text-sm leading-relaxed">{value}</div>
    </div>
  );
}

function Section({ title, content }) {
  if (!content) return null;
  return (
    <div className="bg-secondary/50 rounded-lg p-3">
      <div className="font-bold text-sm mb-1">{title}</div>
      <div className="text-sm whitespace-pre-wrap leading-relaxed">{content}</div>
    </div>
  );
}
